import { createRouter, createWebHistory } from '@ionic/vue-router';
import type { RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/splash' },
  {
    path: '/splash',
    name: 'splash',
    component: () => import('@/views/SplashView.vue'),
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/inventory',
    name: 'inventory-hub',
    component: () => import('@/views/InventoryHubView.vue'),
  },
  {
    path: '/inventory/list',
    name: 'inventory',
    component: () => import('@/views/InventoryView.vue'),
  },
  {
    path: '/inventory/assets',
    name: 'inventory-assets',
    component: () => import('@/views/AssetsView.vue'),
  },
  {
    path: '/inventory/assets/:id',
    name: 'inventory-asset-detail',
    component: () => import('@/views/AssetDetailView.vue'),
    props: true,
  },
  {
    path: '/inventory/categories',
    name: 'inventory-categories',
    component: () => import('@/views/CategoriesView.vue'),
  },
  {
    path: '/inventory/schedules',
    name: 'inventory-schedules',
    component: () => import('@/views/SchedulesView.vue'),
  },
  {
    path: '/orders',
    name: 'orders',
    component: () => import('@/views/OrdersView.vue'),
  },
  {
    path: '/pickups',
    name: 'pickups',
    component: () => import('@/views/PickupListView.vue'),
  },
  {
    path: '/returns',
    name: 'returns',
    component: () => import('@/views/ReturnListView.vue'),
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/views/PosCheckoutView.vue'),
  },
  {
    path: '/orders/new',
    name: 'new-order',
    component: () => import('@/views/NewOrderView.vue'),
  },
  {
    // Where a Transact/Touchnet payment for a JUST-CREATED order redirects back to on success
    // (PaymentPanel.vue's triggerPostOrderPayment()) — a plain path segment rather than a query
    // string specifically because Touchnet's own success-URL scheme (CLI: `link.split('/').join('@')`)
    // doesn't escape '?'/'=' at all, so a query string embedded in it would get corrupted once
    // the backend's own redirect chain treats it as a raw URL segment. Same component as New
    // Order itself — just tells it to jump straight to the existing success/confirmation screen
    // for this order id instead of starting a fresh cart.
    path: '/orders/new/paid/:id',
    name: 'new-order-paid',
    component: () => import('@/views/NewOrderView.vue'),
  },
  {
    path: '/orders/:id',
    name: 'order-detail',
    component: () => import('@/views/OrderDetailView.vue'),
    props: true,
  },
  {
    path: '/orders/:id/checkout',
    name: 'order-checkout',
    component: () => import('@/views/CheckoutView.vue'),
    props: true,
  },
  {
    path: '/orders/:id/checkin',
    name: 'order-checkin',
    component: () => import('@/views/CheckInView.vue'),
    props: true,
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
  },
  {
    path: '/profile/edit',
    name: 'profile-edit',
    component: () => import('@/views/ProfileEditView.vue'),
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import('@/views/CalendarView.vue'),
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/views/CustomersView.vue'),
  },
  {
    path: '/customers/:id',
    name: 'customer-detail',
    component: () => import('@/views/CustomerDetailView.vue'),
    props: true,
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
  },
  {
    path: '/settings/notifications',
    name: 'notification-settings',
    component: () => import('@/views/NotificationSettingsView.vue'),
  },
  {
    path: '/detail/:id',
    name: 'detail',
    component: () => import('@/views/DetailView.vue'),
    props: true,
  },
  {
    path: '/products/:id/edit',
    name: 'product-edit',
    component: () => import('@/views/ProductEditView.vue'),
    props: true,
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/ReportsView.vue'),
  },
  {
    path: '/reports/sales',
    name: 'report-sales',
    component: () => import('@/views/reports/SalesReportView.vue'),
  },
  {
    path: '/reports/my-day',
    name: 'report-my-day',
    component: () => import('@/views/reports/MyDayReportView.vue'),
  },
  {
    path: '/reports/additional-charge',
    name: 'report-additional-charge',
    component: () => import('@/views/reports/FeesChargesReportView.vue'),
  },
  {
    path: '/reports/tax',
    name: 'report-tax',
    component: () => import('@/views/reports/TaxReportView.vue'),
  },
  {
    path: '/reports/on-rent',
    name: 'report-on-rent',
    component: () => import('@/views/reports/OnRentReportView.vue'),
  },
  {
    path: '/reports/inventory-onhand',
    name: 'report-inventory-onhand',
    component: () => import('@/views/reports/InventoryOnHandReportView.vue'),
  },
  {
    path: '/reports/inventory-by-locations',
    name: 'report-inventory-by-locations',
    component: () => import('@/views/reports/InventoryByLocationsView.vue'),
  },
  {
    path: '/reports/coupon-usage',
    name: 'report-coupon-usage',
    component: () => import('@/views/reports/CouponUsageReportView.vue'),
  },
  {
    path: '/reports/customer',
    name: 'report-customer',
    component: () => import('@/views/reports/CustomerReportView.vue'),
  },
  {
    path: '/reports/transactions',
    name: 'report-transactions',
    component: () => import('@/views/reports/TransactionsReportView.vue'),
  },
  {
    path: '/reports/invoices',
    redirect: { name: 'report-transactions' },
  },
  {
    path: '/reports/invoices/:id',
    name: 'report-invoice-detail',
    component: () => import('@/views/reports/InvoiceDetailView.vue'),
    props: true,
  },
  {
    path: '/reports/commission',
    name: 'report-commission',
    component: () => import('@/views/reports/CommissionReportView.vue'),
  },
  {
    path: '/reports/abandoned-cart',
    name: 'report-abandoned-cart',
    component: () => import('@/views/reports/AbandonedCartView.vue'),
  },
  {
    path: '/reports/asset-reconciliation',
    name: 'report-asset-reconciliation',
    component: () => import('@/views/reports/AssetReconciliationView.vue'),
  },
  {
    path: '/reports/wish-list',
    name: 'report-wish-list',
    component: () => import('@/views/reports/WishListView.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Public routes don't require a logged-in session.
const PUBLIC_ROUTES = new Set(['splash', 'login']);

// There was no global guard at all — logging out only did router.replace('/login'),
// which doesn't erase the authenticated pages already in the webview's history
// stack. Without this check, an iOS back-swipe (or Android back button) popped
// straight back into one of those pages and rendered it with no re-check, even
// though auth_token had already been cleared. This guard runs on every
// navigation, including history-driven ones, so that can't happen anymore.
router.beforeEach((to) => {
  const isAuthenticated = !!localStorage.getItem('auth_token');
  const isPublic = typeof to.name === 'string' && PUBLIC_ROUTES.has(to.name);

  if (!isAuthenticated && !isPublic) {
    return { name: 'login' };
  }
  if (isAuthenticated && to.name === 'login') {
    return { name: 'home' };
  }
  return true;
});

export default router;
